import React, { useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import ReCAPTCHA from "react-google-recaptcha";
import { isMobileHost } from "../utils/mobileHost";
import { PinInput } from "../components/PinInput";
import { useMobileOrTablet } from "../hooks/use-mobile-or-tablet";
import { getPinDevice, clearPinDevice, PinDevice } from "../utils/pinDevice";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  Loader2,
  ShieldCheck,
  Building2,
  Users,
  BarChart3,
} from "lucide-react";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, pinLogin } = useAuth();

  const from = (location.state as any)?.from?.pathname || "/dashboard";
  const search = (location.state as any)?.from?.search || "";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});

  // PIN login (mobile/tablet only, when this device has a PIN set up)
  const isMobileOrTablet = useMobileOrTablet();
  const [pinDevice, setPinDevice] = useState<PinDevice | null>(() => getPinDevice());
  const [usePasswordForm, setUsePasswordForm] = useState(false);
  const [pin, setPin] = useState("");
  const [pinLoading, setPinLoading] = useState(false);
  const showPinEntry = !!pinDevice && isMobileOrTablet && !usePasswordForm;

  const redirectAfterLogin = (user: any, permissions: string[]) => {
    if (user.status === "Onboarding") {
      navigate("/my-onboarding", { replace: true });
      return;
    }
    const hasDashboardAccess = permissions.includes("read:dashboard");
    const isDefaultRedirect = !(location.state as any)?.from?.pathname;
    // m.idarah.io: land on the square-apps grid instead of the dashboard
    if (isDefaultRedirect && isMobileHost()) {
      navigate("/home", { replace: true });
    } else if (isDefaultRedirect && !hasDashboardAccess) {
      navigate(`/people/${user.id}`, { replace: true });
    } else {
      navigate(from + search, { replace: true });
    }
  };

  const handlePinComplete = async (value: string) => {
    const finalPin = value || pin;
    if (!pinDevice || pinLoading || finalPin.length !== 6) return;
    setPinLoading(true);

    try {
      const { success, user, permissions, code, message } = await pinLogin(
        pinDevice.deviceId,
        finalPin
      );
      if (success && user) {
        redirectAfterLogin(user, permissions);
        return;
      }
      if (code === "PIN_DEVICE_REVOKED") {
        // Server revoked this device's PIN (too many failures or password
        // change) — the stored deviceId is useless, drop it for good.
        clearPinDevice();
        setPinDevice(null);
        setPin("");
        toast.error("PIN sign-in was removed for this device. Please sign in with your password.");
        return;
      }
      toast.error(message || "Invalid PIN");
      setPin("");
    } finally {
      setPinLoading(false);
    }
  };

  const handleSwitchAccount = () => {
    clearPinDevice();
    setPinDevice(null);
    setPin("");
  };

  // Forgot password state
  const [showForgotDialog, setShowForgotDialog] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const validate = () => {
    const next: { email?: string; password?: string } = {};
    if (!email.trim()) {
      next.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      next.email = "Enter a valid email address";
    }
    if (!password) {
      next.password = "Password is required";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    if (!captchaToken) {
      toast.error("Please complete the reCAPTCHA");
      return;
    }

    setLoading(true);

    try {
      console.log("Attempting login with:", email);
      const { success, user, permissions } = await login(
        email,
        password,
        captchaToken
      );
      if (success && user) {
        redirectAfterLogin(user, permissions);
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("An error occurred during login");
      // Reset reCAPTCHA on error
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    } finally {
      setLoading(false);
    }
  };

  const onCaptchaChange = (token: string | null) => {
    setCaptchaToken(token);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!forgotEmail) {
      toast.error("Please enter your email address");
      return;
    }

    setForgotLoading(true);

    try {
      const response = await fetch(
        `${
          process.env.REACT_APP_API_BASE_URL || "http://localhost:5001"
        }/api/users/forgot-password`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: forgotEmail }),
        }
      );

      const data = await response.json();

      // Always show success to prevent email enumeration
      setForgotSent(true);
      setForgotEmail("");
    } catch (error) {
      console.error("Forgot password error:", error);
      // Still show success to prevent email enumeration
      setForgotSent(true);
      setForgotEmail("");
    } finally {
      setForgotLoading(false);
    }
  };

  const openForgotDialog = () => {
    setForgotSent(false);
    setForgotEmail("");
    setShowForgotDialog(true);
  };

  const closeForgotDialog = () => {
    setShowForgotDialog(false);
    setForgotSent(false);
    setForgotEmail("");
  };

  const highlights = [
    {
      icon: <Users size={18} />,
      title: "People & HR",
      text: "One source of truth for your whole team.",
    },
    {
      icon: <BarChart3 size={18} />,
      title: "Operations & Finance",
      text: "Connected workflows, no scattered sheets.",
    },
    {
      icon: <ShieldCheck size={18} />,
      title: "Governance & Compliance",
      text: "Policies, procedures and assets in one place.",
    },
  ];

  return (
    <div className="min-h-screen w-full flex bg-background">
      {/* ---------- Brand Panel (hidden on mobile) ---------- */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-slate-900 text-white">
        {/* Decorative gradient blobs */}
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-slate-700/40 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-slate-600/30 blur-3xl" />

        <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 w-full">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-slate-900">
              <Building2 size={22} />
            </div>
            <span className="text-2xl font-bold tracking-tight">idarah</span>
          </div>

          {/* Headline */}
          <div className="space-y-6 max-w-md">
            <h1 className="text-4xl xl:text-5xl font-bold leading-tight">
              The operating system for your entire organisation.
            </h1>
            <p className="text-slate-300 text-base leading-relaxed">
              People, operations, finance, assets and governance — unified in one
              connected platform. Nothing fragmented, everything in sync.
            </p>

            <div className="space-y-4 pt-4">
              {highlights.map((h) => (
                <div key={h.title} className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-white/10 text-white">
                    {h.icon}
                  </div>
                  <div>
                    <p className="text-sm font-semibold">{h.title}</p>
                    <p className="text-xs text-slate-400">{h.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <p className="text-xs text-slate-500">
            © {new Date().getFullYear()} Idarah. All rights reserved.
          </p>
        </div>
      </div>

      {/* ---------- Form Panel ---------- */}
      <div className="flex w-full lg:w-1/2 items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex lg:hidden items-center gap-3 mb-8">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Building2 size={20} />
            </div>
            <span className="text-xl font-bold tracking-tight text-foreground">
              idarah
            </span>
          </div>

          {showPinEntry && pinDevice ? (
            /* ---------- PIN entry (mobile/tablet, returning device) ---------- */
            <div className="bg-card rounded-lg border border-border shadow-sm p-8">
              <div className="mb-8 text-center">
                <h2 className="text-2xl font-bold text-foreground">
                  Welcome back{pinDevice.name ? `, ${pinDevice.name.split(" ")[0]}` : ""}
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Enter your 6-digit PIN to continue
                </p>
              </div>

              <div className="space-y-6">
                <PinInput
                  value={pin}
                  onChange={setPin}
                  onComplete={handlePinComplete}
                  disabled={pinLoading}
                />
                {pinLoading && (
                  <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    <Loader2 size={16} className="animate-spin" />
                    Signing in...
                  </div>
                )}
                <div className="flex flex-col items-center gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setUsePasswordForm(true)}
                    className="text-sm font-semibold text-primary hover:underline"
                  >
                    Use password instead
                  </button>
                  <button
                    type="button"
                    onClick={handleSwitchAccount}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Not {pinDevice.name || pinDevice.email}? Switch account
                  </button>
                </div>
              </div>
            </div>
          ) : (
          <div className="bg-card rounded-lg border border-border shadow-sm p-8">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-foreground">Welcome back</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Enter your credentials to access your account
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email */}
              <div className="space-y-1.5">
                <label
                  htmlFor="email"
                  className="text-sm font-bold text-foreground"
                >
                  Email
                </label>
                <div className="relative">
                  <Mail
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (errors.email)
                        setErrors((p) => ({ ...p, email: undefined }));
                    }}
                    className={`pl-10 h-11 rounded-xl ${
                      errors.email
                        ? "border-destructive/40 focus-visible:ring-destructive/30"
                        : ""
                    }`}
                  />
                </div>
                {errors.email && (
                  <p className="text-xs text-destructive font-medium">
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Password */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label
                    htmlFor="password"
                    className="text-sm font-bold text-foreground"
                  >
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={openForgotDialog}
                    className="text-sm font-semibold text-primary hover:underline"
                  >
                    Forgot Password?
                  </button>
                </div>
                <div className="relative">
                  <Lock
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (errors.password)
                        setErrors((p) => ({ ...p, password: undefined }));
                    }}
                    className={`pl-10 pr-10 h-11 rounded-xl ${
                      errors.password
                        ? "border-destructive/40 focus-visible:ring-destructive/30"
                        : ""
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-muted-foreground"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <EyeOff size={18} />
                    ) : (
                      <Eye size={18} />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-xs text-destructive font-medium">
                    {errors.password}
                  </p>
                )}
              </div>

              {/* reCAPTCHA */}
              <div className="flex justify-center pt-1">
                {process.env.REACT_APP_RECAPTCHA_SITE_KEY ? (
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    onChange={onCaptchaChange}
                  />
                ) : (
                  <div className="text-destructive text-sm">
                    reCAPTCHA configuration missing
                  </div>
                )}
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin mr-2" />
                    Signing in...
                  </>
                ) : (
                  "Continue"
                )}
              </Button>

              {/* Back to PIN entry when this device has one */}
              {pinDevice && isMobileOrTablet && (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setUsePasswordForm(false)}
                    className="text-sm font-semibold text-primary hover:underline"
                  >
                    Use PIN instead
                  </button>
                </div>
              )}
            </form>
          </div>
          )}

          <p className="text-center text-xs text-muted-foreground mt-6">
            Protected by enterprise-grade security. Need access?{" "}
            <button
              onClick={() => navigate("/contact")}
              className="font-semibold text-muted-foreground hover:text-foreground"
            >
              Contact your administrator
            </button>
          </p>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotDialog} onOpenChange={setShowForgotDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Enter your email address and we'll send you a link to reset your
              password.
            </DialogDescription>
          </DialogHeader>

          {forgotSent ? (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-800">
                  If an account exists with this email, a password reset link has
                  been sent.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <label
                    htmlFor="forgotEmail"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Email
                  </label>
                  <Input
                    id="forgotEmail"
                    type="email"
                    value={forgotEmail}
                    placeholder="name@example.com"
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeForgotDialog}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={forgotLoading}>
                  {forgotLoading ? "Sending..." : "Send Reset Link"}
                </Button>
              </DialogFooter>
            </form>
          )}

          {forgotSent && (
            <DialogFooter>
              <Button type="button" onClick={closeForgotDialog}>
                Close
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
